'use client';

import { AnimatePresence, motion } from 'framer-motion';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';

// 클라이언트 사이드에서만 로드할 컴포넌트들
const Lottie = dynamic(() => import('lottie-react'), { ssr: false });

interface IndicatorProps {
  message?: string;
  subMessage?: string;
  isOpen?: boolean;
  waitForAnimationCycle?: boolean;
}

export default function Indicator({
  message = '',
  subMessage = '',
  isOpen = true,
  waitForAnimationCycle = false,
}: IndicatorProps) {
  const ANIMATION_DURATION = 3000;

  const [animationIndex, setAnimationIndex] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [actuallyOpen, setActuallyOpen] = useState(isOpen);
  const [lastChangeTime, setLastChangeTime] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [animations, setAnimations] = useState<any[]>([]);

  useEffect(() => {
    // 동적으로 애니메이션 데이터 로드
    const loadAnimations = async () => {
      if (typeof window === 'undefined') return;

      const [ani1, ani2, ani3] = await Promise.all([
        import('@/assets/animations/1.json'),
        import('@/assets/animations/2.json'),
        import('@/assets/animations/3.json')
      ]);
      setAnimations([ani1.default, ani2.default, ani3.default]);
    };

    loadAnimations();
  }, []);

  const messages = [
    {
      main: '프리미엄 수입육을 준비 중입니다',
      sub: '엄선된 글로벌 공급자의 최상급 육류 상품을 곧 만나보세요',
    },
    {
      main: '최적의 가격을 분석하고 있어요',
      sub: '품질은 높게, 단가는 합리적으로! 최상의 거래 조건을 제안합니다',
    },
    {
      main: '해외 수입 정보를 확인 중입니다',
      sub: '원산지부터 통관까지, 투명한 이력을 제공하는 글로벌 수입육',
    },
  ];

  // 배열을 섞는 함수
  const shuffleArray = () => {
    const indices = [0, 1, 2];
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  };

  // 애니메이션 시작 함수
  const startAnimation = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // 시작할 때 순서를 섞고 첫번째 인덱스로 시작
    const shuffled = shuffleArray();
    setAnimationIndex(shuffled[0]);
    setLastChangeTime(Date.now());
  };

  // 애니메이션 정지 함수
  const stopAnimation = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // 닫기 처리 함수
  const handleClose = () => {
    if (waitForAnimationCycle && !isClosing) {
      setIsClosing(true);

      // 현재 메시지가 노출된 시간을 계산
      const elapsedTime = Date.now() - lastChangeTime;

      // 남은 시간 계산 (최소 0ms)
      const remainingTime = Math.max(0, ANIMATION_DURATION - elapsedTime);

      closeTimeoutRef.current = setTimeout(() => {
        stopAnimation();
        setActuallyOpen(false);
        setIsClosing(false);
      }, remainingTime);
    } else {
      stopAnimation();
      setActuallyOpen(false);
    }
  };

  // isOpen 상태 변경에 따른 처리
  useEffect(() => {
    if (isOpen) {
      setActuallyOpen(true);
      startAnimation();
      // 이미 닫기가 예약되어 있었다면 취소
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
        setIsClosing(false);
      }
    } else {
      handleClose();
    }

    return () => {
      stopAnimation();
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // 현재 메시지
  const currentMessage = message || messages[animationIndex].main;
  const currentSubMessage = subMessage || messages[animationIndex].sub;

  if (typeof window === 'undefined' || !animations.length) {
    return null;
  }

  return (
    <AnimatePresence>
      {actuallyOpen && (
        <div
          className={`fixed inset-0 z-50 overflow-hidden pointer-events-none`}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isClosing ? 0 : 1 }}
            exit={{ opacity: 0, pointerEvents: 'none' }}
            transition={{
              duration: 0.5,
              ease: 'easeOut',
            }}
            className={`absolute inset-0 flex items-center justify-center pb-40 z-50 bg-black/30 ${isClosing ? 'pointer-events-none' : 'pointer-events-auto'}`}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{
                duration: 0.4,
                ease: 'easeOut',
              }}
              className="bg-white w-[90%] max-w-94 min-h-67.25 px-10 pt-12 pb-8 rounded-2xl shadow-lg text-center text-black flex flex-col items-center pointer-events-auto"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-8"
              >
                <div className="relative w-24 h-24 mx-auto">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={animationIndex}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      {animations[animationIndex] && (
                        <motion.div
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <Lottie animationData={animations[animationIndex]} style={{ width: 64, height: 64 }} />
                        </motion.div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>
              <motion.div
                key={currentMessage}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                <h2 className="title-20b text-(--secondary-800) mb-2">
                  {currentMessage}
                </h2>
                <p className="body-16r text-(--secondary-600)">
                  {currentSubMessage}
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
